import { notification } from 'antd';
import { useCallback, useEffect, useRef } from 'react';

import { useRestoreEstimateData } from './useRestoreEstimateData';

import { LOCAL_STORAGE_KEY, STATUS } from '~/core/constants';
import { guidToShortGuid, notifyFromStatus } from '~/core/helpers';
import { setLocalData } from '~/core/helpers/storageHelper';
import { LoadEstimateKeys } from '~/core/hooks/useEstimate';
import useExpiredEstimateActions from '~/core/hooks/useExpiredEstimateActions';
import useFetchedEstimate from '~/core/hooks/useFetchedEstimate';
import {
  EstimateStatus,
  GetEstimateDto,
} from '~/core/models/save-estimate-dto';
import { isValidZipCode } from '~/core/utils/validate';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  selectCurrentEstimateCode,
  selectIsSavingEstimate,
  updateCurrentEstimate,
  updateEeStatusList,
  updateLoadEstimateKeys,
  updateShowListOfEstimates,
} from '~/redux/slices/estimate';
import { updateZipcode } from '~/redux/slices/zipcode';
import { getNotificationsFor } from '~legacy/components';

export const useEstimateLoader = (loadEstimateCode: string | undefined) => {
  const {
    fetchedEstimate,
    isFetchingEstimate,
    isError: isFetchError,
  } = useFetchedEstimate();

  const dispatch = useAppDispatch();
  const isSavingEstimate = useAppSelector(selectIsSavingEstimate);
  const currentEstimateCode = useAppSelector(selectCurrentEstimateCode);
  const { restoreFromFetchedEstimate } = useRestoreEstimateData();
  const { openToNewEstimate, createCloneEstimate } =
    useExpiredEstimateActions();
  const isEstimateFetched = useRef<boolean>(false);
  const estimateLoaded = useRef<boolean>(false);

  const loadEstimateAndEnableSteps = useCallback(
    (loadedEstimateDto: GetEstimateDto) => {
      restoreFromFetchedEstimate(loadedEstimateDto);
      dispatch(updateShowListOfEstimates(true));
      setLocalData(LOCAL_STORAGE_KEY.KEEP_LOAD_ESTIMATE_IN_URL, true);
    },
    [dispatch, restoreFromFetchedEstimate]
  );

  const resetLoadedFlags = useCallback(() => {
    isEstimateFetched.current = false;
    estimateLoaded.current = false;
  }, []);

  useEffect(() => {
    if (loadEstimateCode) {
      dispatch(
        updateLoadEstimateKeys({
          code: guidToShortGuid(loadEstimateCode),
        } as LoadEstimateKeys)
      );
    }
  }, [dispatch, loadEstimateCode]);

  // Update page title when estimate changes
  useEffect(() => {
    if (fetchedEstimate) {
      document.title = `${fetchedEstimate.customerName}: ${fetchedEstimate.estimateNumber}`;
    }
  }, [fetchedEstimate]);

  useEffect(() => {
    const isInProcessOrError =
      isFetchingEstimate || isFetchError || isSavingEstimate;

    if (fetchedEstimate && (isEstimateFetched.current || !isInProcessOrError)) {
      if (fetchedEstimate.estimateData) {
        dispatch(updateCurrentEstimate(fetchedEstimate.estimateData));
      }

      isEstimateFetched.current = true;
      if (isValidZipCode(fetchedEstimate.customerZip)) {
        dispatch(updateZipcode(fetchedEstimate.customerZip));
      }
    }
  }, [
    dispatch,
    fetchedEstimate,
    isFetchError,
    isFetchingEstimate,
    isSavingEstimate,
  ]);

  useEffect(() => {
    if (isEstimateFetched.current && !fetchedEstimate) {
      isEstimateFetched.current = false;
      estimateLoaded.current = false;
      dispatch(updateShowListOfEstimates(false));
    }
  }, [dispatch, fetchedEstimate]);

  // Use refs to track processed estimates and prevent duplicate processing
  const processedEstimateRef = useRef<string | null>(null);
  const lastLoadEstimateCodeRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Early return if we're still fetching or have an error
    if (isFetchingEstimate || isFetchError) {
      return;
    }

    // Early return if no estimate or estimate data
    if (!fetchedEstimate || !fetchedEstimate.estimateData) {
      return;
    }

    // Early return if estimate not fetched yet
    if (!isEstimateFetched.current) {
      return;
    }

    // Early return if no load estimate code
    if (!loadEstimateCode) {
      return;
    }

    const normalizedLoadCode = guidToShortGuid(loadEstimateCode);

    // Early return if already processed this exact estimate
    const estimateKey = `${normalizedLoadCode}-${fetchedEstimate.estimateNumber}`;

    if (processedEstimateRef.current === estimateKey) {
      return;
    }

    // Early return if already loaded or codes match
    if (estimateLoaded.current || normalizedLoadCode === currentEstimateCode) {
      return;
    }

    // Mark as processed immediately to prevent duplicate runs
    processedEstimateRef.current = estimateKey;
    lastLoadEstimateCodeRef.current = loadEstimateCode;

    loadEstimateAndEnableSteps(fetchedEstimate);
    getNotificationsFor(fetchedEstimate?.estimateNumber); // EE3-756 - load any _saved_ notifications for the estimate

    const makeCurrentStatus = (estimate: GetEstimateDto): EstimateStatus => ({
      imeMessage: null,
      imeStatus: null,
      status: estimate.status,
      paymentUrl: estimate.paymentUrl || null,
    });

    // notify based on the fetched estimate status
    const translationSection =
      fetchedEstimate.status === STATUS.EXPIRED ? 'EXPIRED' : 'CLOSED';

    const statusList = notifyFromStatus({
      currentStatus: makeCurrentStatus(fetchedEstimate),
      estimate: fetchedEstimate,
      onCopyEstimate: () => {
        notification.destroy();
        createCloneEstimate(translationSection);
      },
      onNewEstimate: () => {
        notification.destroy();
        openToNewEstimate();
      },
    });

    // and update the list of statuses in state (not sure if these are used)
    dispatch(updateEeStatusList(statusList));

    estimateLoaded.current = true;
    setLocalData(LOCAL_STORAGE_KEY.FIRST_SHAPE_LOAD, false);
  }, [
    createCloneEstimate,
    currentEstimateCode,
    dispatch,
    fetchedEstimate,
    isFetchError,
    isFetchingEstimate,
    loadEstimateAndEnableSteps,
    loadEstimateCode,
    openToNewEstimate,
  ]);

  // Reset processed ref when loadEstimateCode changes to a different value
  useEffect(() => {
    if (
      loadEstimateCode !== lastLoadEstimateCodeRef.current &&
      loadEstimateCode !== undefined
    ) {
      processedEstimateRef.current = null;
    }
  }, [loadEstimateCode]);

  return {
    fetchedEstimate,
    isFetchError,
    isFetchingEstimate,
    resetLoadedFlags,
  };
};
